import { type AiCapability } from '../../ai/ai-capability/ai-capability';
import { aiModelsFor } from '../../ai/ai-model-catalogue/ai-model-catalogue';
import {
  MAX_VERIFICATION_CODE_LENGTH,
  MIN_VERIFICATION_CODE_LENGTH,
} from '../../auth/contact-verification/verification-state/verification-state';
import {
  INTEGRATION_CAPABILITIES,
  type IntegrationCapability,
} from '../integration-capability/integration-capability';
import { type IntegrationFieldDescriptor } from '../integration-field/integration-field';
import {
  findProviderField,
  type IntegrationProviderDescriptor,
} from '../integration-provider/integration-provider';

/** Every provider of one capability. Exactly one of them is active at a time. */
export interface IntegrationCapabilityDescriptor {
  capability: IntegrationCapability;
  providers: readonly IntegrationProviderDescriptor[];
}

const secret = (
  key: string,
  extra: Partial<IntegrationFieldDescriptor> = {},
): IntegrationFieldDescriptor => ({
  key,
  type: 'password',
  secret: true,
  required: true,
  ...extra,
});

const text = (
  key: string,
  extra: Partial<IntegrationFieldDescriptor> = {},
): IntegrationFieldDescriptor => ({ key, type: 'text', secret: false, required: true, ...extra });

/**
 * The AI `model` field — a curated list, **derived** from the model catalogue
 * rather than restated here, so the models an operator can pick and the models
 * the rate table prices are one list (U3).
 *
 * `select` does not forbid a value outside the list: a vendor ships a model
 * faster than a deployment ships a release. See `integrationFieldValueSchema`.
 */
const model = (
  capability: AiCapability,
  provider: string,
  envVar: string,
): IntegrationFieldDescriptor => ({
  key: 'model',
  type: 'select',
  secret: false,
  required: true,
  envVar,
  options: aiModelsFor(capability, provider),
});

/**
 * The **capability catalog** (N21) — the only place an integration field comes
 * into existence.
 *
 * Everything downstream is derived from this list and nothing else: the admin
 * form (W9), the write validation, the masked status view, the RBAC permission
 * `integrations.<capability>.manage`, and the env fallback names. Adding a
 * provider is one entry here plus one adapter in the owning layer — no UI code,
 * no migration, no new permission literal.
 *
 * `envVar` names are the **fallback** the vault's resolver reads when nothing is
 * stored, so a fresh deployment works before staff configure anything. They are
 * read there and nowhere else (U14/N21): a consumer that reads a credential
 * from `process.env` is a rule violation, and the api's ESLint law refuses it.
 */
const CATALOG: readonly IntegrationCapabilityDescriptor[] = [
  {
    capability: 'ai-text',
    providers: [
      {
        key: 'gemini',
        wired: true,
        fields: [
          secret('apiKey', { envVar: 'GEMINI_API_KEY' }),
          model('ai-text', 'gemini', 'AI_TEXT_MODEL'),
        ],
      },
      {
        key: 'anthropic',
        wired: true,
        fields: [
          secret('apiKey', { envVar: 'ANTHROPIC_API_KEY' }),
          model('ai-text', 'anthropic', 'AI_TEXT_MODEL'),
        ],
      },
    ],
  },
  {
    capability: 'ai-embedding',
    providers: [
      {
        key: 'gemini',
        wired: true,
        fields: [
          secret('apiKey', { envVar: 'GEMINI_API_KEY' }),
          model('ai-embedding', 'gemini', 'AI_EMBEDDING_MODEL'),
        ],
      },
    ],
  },
  {
    // The advisory face match (P13b). Separate from `ai-embedding` because the
    // input is an image, and because an operator points face-match and semantic
    // search at different models.
    capability: 'face-embedding',
    providers: [
      {
        key: 'gemini',
        wired: true,
        fields: [
          secret('apiKey', { envVar: 'GEMINI_API_KEY' }),
          model('face-embedding', 'gemini', 'AI_FACE_EMBEDDING_MODEL'),
        ],
      },
    ],
  },
  {
    // The AI-generated-photo advisory (P27b1): a vision classification, so a
    // capability of its own — not an embedding, and not text.
    capability: 'ai-image',
    providers: [
      {
        key: 'gemini',
        wired: true,
        fields: [
          secret('apiKey', { envVar: 'GEMINI_API_KEY' }),
          model('ai-image', 'gemini', 'AI_IMAGE_MODEL'),
        ],
      },
      {
        key: 'anthropic',
        wired: true,
        fields: [
          secret('apiKey', { envVar: 'ANTHROPIC_API_KEY' }),
          model('ai-image', 'anthropic', 'AI_IMAGE_MODEL'),
        ],
      },
    ],
  },
  {
    capability: 'storage',
    providers: [
      {
        // The dev stack's MinIO: an S3-compatible endpoint is mandatory, and a
        // region is not (MinIO ignores it).
        key: 'minio',
        wired: true,
        fields: [
          secret('accessKeyId', { envVar: 'S3_ACCESS_KEY_ID' }),
          secret('secretAccessKey', { envVar: 'S3_SECRET_ACCESS_KEY' }),
          text('bucket', { envVar: 'S3_BUCKET' }),
          { key: 'endpoint', type: 'url', secret: false, required: true, envVar: 'S3_ENDPOINT' },
          text('region', { required: false, envVar: 'S3_REGION' }),
          {
            // The externally reachable endpoint used to **sign** URLs a client
            // can actually reach (the api may talk to storage over an internal
            // hostname). Not a public bucket — there is none, ever (N12).
            key: 'publicBaseUrl',
            type: 'url',
            secret: false,
            required: false,
            envVar: 'S3_PUBLIC_BASE_URL',
          },
        ],
      },
      {
        key: 's3',
        wired: true,
        fields: [
          secret('accessKeyId', { envVar: 'S3_ACCESS_KEY_ID' }),
          secret('secretAccessKey', { envVar: 'S3_SECRET_ACCESS_KEY' }),
          text('bucket', { envVar: 'S3_BUCKET' }),
          text('region', { envVar: 'S3_REGION' }),
          { key: 'endpoint', type: 'url', secret: false, required: false, envVar: 'S3_ENDPOINT' },
          {
            key: 'publicBaseUrl',
            type: 'url',
            secret: false,
            required: false,
            envVar: 'S3_PUBLIC_BASE_URL',
          },
        ],
      },
      {
        key: 'r2',
        wired: true,
        fields: [
          secret('accessKeyId', { envVar: 'S3_ACCESS_KEY_ID' }),
          secret('secretAccessKey', { envVar: 'S3_SECRET_ACCESS_KEY' }),
          text('bucket', { envVar: 'S3_BUCKET' }),
          { key: 'endpoint', type: 'url', secret: false, required: true, envVar: 'S3_ENDPOINT' },
          text('region', { required: false, envVar: 'S3_REGION' }),
          {
            key: 'publicBaseUrl',
            type: 'url',
            secret: false,
            required: false,
            envVar: 'S3_PUBLIC_BASE_URL',
          },
        ],
      },
    ],
  },
  {
    capability: 'push',
    providers: [
      {
        key: 'fcm',
        wired: true,
        fields: [
          // A service-account JSON is a ~2 KB document, not a password field —
          // but it is every bit a credential, so it is secret. `json` is what
          // gets it a document editor instead of a one-line masked input,
          // where a truncated paste is undetectable until push fails.
          {
            key: 'serviceAccountJson',
            type: 'json',
            secret: true,
            required: true,
            envVar: 'FCM_SERVICE_ACCOUNT_JSON',
          },
        ],
      },
    ],
  },
  {
    capability: 'email',
    providers: [
      {
        // Wired by P21e1: `SmtpEmailAdapter` (modules/notifications) sends
        // through it and registers the `email` probe. `secure` is implicit TLS
        // (465) when on, plain-then-STARTTLS (587/2525, Mailtrap's sandbox) when
        // off — the per-field help in the admin says so, because the wrong
        // choice fails with the vendor's least helpful sentence.
        key: 'smtp',
        wired: true,
        fields: [
          text('host', { envVar: 'SMTP_HOST' }),
          { key: 'port', type: 'number', secret: false, required: true, envVar: 'SMTP_PORT' },
          { key: 'secure', type: 'boolean', secret: false, required: true, envVar: 'SMTP_SECURE' },
          text('user', { required: false, envVar: 'SMTP_USER' }),
          secret('password', { required: false, envVar: 'SMTP_PASSWORD' }),
          // The sender address. The brand name is added as the display name
          // by the adapter (U16) — never typed here.
          { key: 'from', type: 'email', secret: false, required: true, envVar: 'SMTP_FROM' },
        ],
      },
    ],
  },
  {
    capability: 'revenuecat',
    providers: [
      {
        key: 'revenuecat',
        wired: true,
        fields: [
          // Server secrets only. The **public** SDK keys the mobile apps carry are brand
          // configuration — `brand.iosRcPublicKey` / `brand.androidRcPublicKey`.
          secret('secretApiKey', { envVar: 'REVENUECAT_SECRET_API_KEY' }),
          secret('webhookAuthSecret', { envVar: 'REVENUECAT_WEBHOOK_AUTH_SECRET' }),
        ],
      },
    ],
  },
  {
    // P30b1. One-time codes to phones. Two strategies behind one port: a `send`
    // provider carries the platform's own code as the rendered
    // `auth.phone-verification.code` template; the `managed` one mints, sends and
    // checks the code itself while the platform keeps attempts, cooldowns and the
    // delivery log. Nothing but a challenge ever travels here.
    capability: 'sms',
    providers: [
      {
        // A CST-licensed Saudi aggregator with direct STC/Mobily/Zain routes; the
        // sender name is registered by the owner with the company's CR, so this
        // is the provider for the day the company exists.
        key: 'unifonic',
        wired: true,
        strategy: 'send',
        fields: [
          secret('appSid', { envVar: 'SMS_UNIFONIC_APP_SID' }),
          text('senderId', { envVar: 'SMS_UNIFONIC_SENDER_ID' }),
        ],
      },
      {
        // Twilio Verify delivers from Twilio's own pre-registered senders, so it
        // needs no company — the day-one provider. `codeLength` mirrors the
        // Verify service's own setting (the vendor mints the code); the probe
        // reads the service and refuses a mismatch. Bounded to what the phone's
        // code field can draw.
        key: 'twilio-verify',
        wired: true,
        strategy: 'managed',
        fields: [
          text('accountSid', { envVar: 'SMS_TWILIO_ACCOUNT_SID' }),
          secret('authToken', { envVar: 'SMS_TWILIO_AUTH_TOKEN' }),
          text('verifyServiceSid', { envVar: 'SMS_TWILIO_VERIFY_SERVICE_SID' }),
          {
            key: 'codeLength',
            type: 'number',
            secret: false,
            required: true,
            envVar: 'SMS_TWILIO_CODE_LENGTH',
            min: MIN_VERIFICATION_CODE_LENGTH,
            max: MAX_VERIFICATION_CODE_LENGTH,
          },
        ],
      },
      {
        // Authentica mints, sends and checks the code from its own default sender, so
        // it needs no company either. Length, expiry, fallback channel and templates
        // live in its dashboard; its API reports none of them, so `codeLength` is
        // typed here to match. A blank template id is the application's default.
        key: 'authentica',
        wired: true,
        strategy: 'managed',
        fields: [
          secret('apiKey', { envVar: 'SMS_AUTHENTICA_API_KEY' }),
          {
            key: 'codeLength',
            type: 'number',
            secret: false,
            required: true,
            envVar: 'SMS_AUTHENTICA_CODE_LENGTH',
            min: MIN_VERIFICATION_CODE_LENGTH,
            max: MAX_VERIFICATION_CODE_LENGTH,
          },
          {
            key: 'templateIdAr',
            type: 'number',
            secret: false,
            required: false,
            envVar: 'SMS_AUTHENTICA_TEMPLATE_ID_AR',
            min: 1,
          },
          {
            key: 'templateIdEn',
            type: 'number',
            secret: false,
            required: false,
            envVar: 'SMS_AUTHENTICA_TEMPLATE_ID_EN',
            min: 1,
          },
        ],
      },
      {
        // Development only: the message is written to the api log, where the
        // developer reads the code. Nothing to configure; activation is refused
        // outside `development | test`.
        key: 'log',
        wired: true,
        strategy: 'send',
        devOnly: true,
        fields: [],
      },
    ],
  },
];

export const INTEGRATION_CATALOG: readonly IntegrationCapabilityDescriptor[] = CATALOG;

const BY_CAPABILITY: ReadonlyMap<string, IntegrationCapabilityDescriptor> = new Map(
  CATALOG.map((entry) => [entry.capability, entry]),
);

/** The capability's descriptor, or `undefined` — the caller turns that into a 404. */
export function getCapabilityDescriptor(
  capability: string,
): IntegrationCapabilityDescriptor | undefined {
  return BY_CAPABILITY.get(capability);
}

/** The provider's descriptor within a capability, or `undefined` (the caller answers 422). */
export function getProviderDescriptor(
  capability: string,
  provider: string,
): IntegrationProviderDescriptor | undefined {
  return getCapabilityDescriptor(capability)?.providers.find((entry) => entry.key === provider);
}

/** One field of one provider, or `undefined` — an undeclared field is never written. */
export function getFieldDescriptor(
  capability: string,
  provider: string,
  fieldKey: string,
): IntegrationFieldDescriptor | undefined {
  const descriptor = getProviderDescriptor(capability, provider);
  return descriptor === undefined ? undefined : findProviderField(descriptor, fieldKey);
}

/** Whether this capability declares this provider. */
export function isKnownProvider(capability: string, provider: string): boolean {
  return getProviderDescriptor(capability, provider) !== undefined;
}

/**
 * The RBAC key that governs one capability (N21/N22).
 *
 * Derived, never written by hand: the permission catalog appends these so the
 * guard, the boot seed and the admin role editor all read the same list.
 */
export function capabilityManagePermission(
  capability: IntegrationCapability,
): `integrations.${IntegrationCapability}.manage` {
  return `integrations.${capability}.manage`;
}

/** Every derived capability permission — what the access catalog folds in. */
export const INTEGRATION_MANAGE_PERMISSIONS = INTEGRATION_CAPABILITIES.map((capability) =>
  capabilityManagePermission(capability),
) as readonly `integrations.${IntegrationCapability}.manage`[];
